import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import {
  RefreshCw, KeyRound, PlugZap, ShieldCheck, Save, Wallet, Layers,
  Package, TrendingUp, AlertTriangle, CheckCircle2, ChevronRight, Info,
  Database, Server, Clock, Eye, EyeOff
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, hint, tint = '#ff8247' }) => (
  <div className="glass-card rounded-2xl p-5">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${tint}18` }}>
      <Icon className="w-5 h-5" style={{ color: tint }} />
    </div>
    <div className="text-white text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</div>
    <div className="text-white/50 text-xs mt-1">{label}</div>
    {hint && <div className="text-white/40 text-[10px] mt-2">{hint}</div>}
  </div>
);

const AdminSMMHype = () => {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [form, setForm] = useState({
    endpoint: 'https://smmhype.com/api/v2',
    api_key: '',
    coefficient: 7,
    sync_frequency: 'manual',
    removed_behavior: 'disable'
  });

  const loadAll = async () => {
    try {
      const [cfg, sum] = await Promise.all([
        axios.get(`${API}/admin/smmhype/config`),
        axios.get(`${API}/catalog/summary`)
      ]);
      setConfig(cfg.data);
      setSummary(sum.data);
      setForm(f => ({
        ...f,
        endpoint: cfg.data.endpoint || f.endpoint,
        coefficient: cfg.data.coefficient ?? f.coefficient,
        sync_frequency: cfg.data.sync_frequency || f.sync_frequency,
        removed_behavior: cfg.data.removed_behavior || f.removed_behavior
      }));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveConfig = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { ...form, ...extra };
      if (!payload.api_key) delete payload.api_key;
      const { data } = await axios.post(`${API}/admin/smmhype/config`, payload);
      setConfig(data);
      toast({ title: 'Paramètres enregistrés', description: 'La configuration a été sauvegardée.' });
      setForm(f => ({ ...f, api_key: '' }));
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/smmhype/test-connection`);
      toast({ title: 'Connexion OK', description: `Solde SMMHype : ${data.balance?.balance ?? '—'} ${data.balance?.currency ?? ''}` });
    } catch (e) {
      toast({ title: 'Connexion échouée', description: e.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/smmhype/sync`);
      toast({ title: 'Synchronisation terminée', description: `${data.stats.created} créés, ${data.stats.updated} mis à jour, ${data.stats.disabled} désactivés.` });
      loadAll();
    } catch (e) {
      toast({ title: 'Sync échouée', description: e.response?.data?.detail || e.message });
    } finally { setSyncing(false); }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await axios.post(`${API}/admin/smmhype/recompute-prices?coefficient=${form.coefficient}`);
      toast({ title: 'Prix recalculés', description: `${data.updated} services mis à jour · coefficient ×${data.coefficient}` });
      loadAll();
    } catch (e) {
      toast({ title: 'Recalcul échoué', description: e.response?.data?.detail || e.message });
    } finally { setRecomputing(false); }
  };

  const apiKeySet = config?.api_key_set;
  const lastSync = summary?.last_sync_at ? new Date(summary.last_sync_at).toLocaleString('fr-FR') : '—';
  const stats = summary?.last_sync_stats || {};

  return (
    <div>
      <AnnouncementBar />
      <Header />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-14">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="px-2.5 py-1 rounded-full bg-[#ff8247]/10 border border-[#ff8247]/20 text-[#ff8247] text-[10px] font-bold uppercase tracking-widest">Admin · SMMHype</div>
              <span className="text-white/40 text-xs">Dernière sync : {lastSync}</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-none">
              Intégration <span className="gradient-text">SMMHype</span>
            </h1>
            <p className="text-white/60 mt-3 max-w-2xl">
              Importation automatique des catégories, services, prix fournisseur, quantités min/max, refill/cancel — puis recalcul du prix de vente selon le coefficient.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/admin/pricing" className="btn-ghost inline-flex items-center gap-2 text-sm">
              <TrendingUp className="w-4 h-4" /> Moteur tarifaire
            </Link>
            <button onClick={syncNow} disabled={syncing || !apiKeySet}
              className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sync…' : 'Synchroniser'}
            </button>
          </div>
        </div>

        {/* Connection status banner */}
        <div className={`rounded-2xl p-4 mb-6 flex items-center gap-3 ${apiKeySet ? 'bg-[#00b67a]/10 border border-[#00b67a]/25' : 'bg-yellow-500/10 border border-yellow-500/25'}`}>
          {apiKeySet ? <CheckCircle2 className="w-5 h-5 text-[#00b67a]" /> : <AlertTriangle className="w-5 h-5 text-yellow-400" />}
          <div className="flex-1">
            <div className={`text-sm font-semibold ${apiKeySet ? 'text-[#00b67a]' : 'text-yellow-300'}`}>
              {apiKeySet ? 'API SMMHype connectée' : 'Clé API SMMHype requise'}
            </div>
            <div className="text-white/60 text-xs">
              {apiKeySet
                ? `Clé actuelle : ${config?.api_key || '****'} · Endpoint : ${config?.endpoint}`
                : 'Colle ta clé API dans le formulaire ci-dessous pour activer la synchronisation.'}
            </div>
          </div>
          {apiKeySet && (
            <button onClick={testConnection} disabled={testing}
              className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-xs font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50">
              <PlugZap className="w-3.5 h-3.5" /> {testing ? 'Test…' : 'Tester la connexion'}
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Layers} label="Catégories importées" value={summary?.categories ?? '—'} hint={`+${stats.categories_created || 0} lors de la dernière sync`} />
          <StatCard icon={Package} label="Services totaux" value={summary?.services ?? '—'} tint="#00b67a" hint={`${summary?.active_services ?? 0} actifs`} />
          <StatCard icon={TrendingUp} label="Coefficient de vente" value={`×${summary?.coefficient ?? form.coefficient}`} tint="#4285F4" hint="Fournisseur × coefficient" />
          <StatCard icon={Clock} label="Dernière sync" value={lastSync.split(' ')[0] || '—'} tint="#ff2d6f" hint={lastSync.split(' ').slice(1).join(' ')} />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Config form */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <KeyRound className="w-5 h-5 text-[#ff8247]" />
              <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Configuration API</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-white/60 text-xs mb-2 block">Endpoint</label>
                <input value={form.endpoint} onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ff8247] transition" />
              </div>
              <div>
                <label className="text-white/60 text-xs mb-2 block flex items-center gap-2">
                  Clé API SMMHype
                  {apiKeySet && <span className="text-[#00b67a] text-[10px] uppercase tracking-widest font-bold">Actuelle : {config.api_key}</span>}
                </label>
                <div className="relative">
                  <input value={form.api_key} type={showKey ? 'text' : 'password'}
                    onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                    placeholder={apiKeySet ? 'Laisser vide pour conserver la clé actuelle' : 'Colle ta clé SMMHype ici'}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-11 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#ff8247] transition" />
                  <button type="button" onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Coefficient (×)</label>
                  <input type="number" step="0.1" min="1" value={form.coefficient}
                    onChange={e => setForm(f => ({ ...f, coefficient: parseFloat(e.target.value) }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ff8247] transition" />
                </div>
                <div>
                  <label className="text-white/60 text-xs mb-2 block">Fréquence</label>
                  <select value={form.sync_frequency}
                    onChange={e => setForm(f => ({ ...f, sync_frequency: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ff8247] transition">
                    <option value="manual">Manuelle uniquement</option>
                    <option value="hourly">Toutes les heures</option>
                    <option value="daily">Une fois par jour</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-white/60 text-xs mb-2 block">Services retirés par SMMHype</label>
                <select value={form.removed_behavior}
                  onChange={e => setForm(f => ({ ...f, removed_behavior: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ff8247] transition">
                  <option value="disable">Désactiver (conservés en base, cachés du catalogue)</option>
                  <option value="mark_out">Marquer épuisé (visibles mais indisponibles)</option>
                  <option value="delete">Supprimer définitivement</option>
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => saveConfig()} disabled={saving}
                  className="btn-primary flex-1 justify-center inline-flex items-center gap-2 text-sm disabled:opacity-50">
                  <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button onClick={recompute} disabled={recomputing || !apiKeySet}
                  className="btn-ghost inline-flex items-center gap-2 text-sm disabled:opacity-50">
                  <RefreshCw className={`w-4 h-4 ${recomputing ? 'animate-spin' : ''}`} /> Recalculer prix
                </button>
              </div>
            </div>
          </div>

          {/* Sync summary */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <Database className="w-5 h-5 text-[#ff8247]" />
              <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Rapport de synchronisation</h3>
            </div>

            {stats && Object.keys(stats).length > 0 ? (
              <div className="space-y-2 text-sm">
                {[
                  ['Services traités', stats.total, Package, '#ff8247'],
                  ['Créés', stats.created, CheckCircle2, '#00b67a'],
                  ['Mis à jour', stats.updated, RefreshCw, '#4285F4'],
                  ['Catégories créées', stats.categories_created, Layers, '#ff2d6f'],
                  ['Désactivés', stats.disabled, AlertTriangle, '#facc15'],
                  ['Supprimés', stats.deleted, AlertTriangle, '#ef4444']
                ].map(([label, value, Icon, color], i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5">
                    <div className="flex items-center gap-2 text-white/70 text-xs">
                      <Icon className="w-4 h-4" style={{ color }} /> {label}
                    </div>
                    <div className="text-white font-bold text-sm">{value ?? 0}</div>
                  </div>
                ))}
                <div className="pt-2 text-white/50 text-xs">
                  Démarré : {stats.started_at ? new Date(stats.started_at).toLocaleString('fr-FR') : '—'}<br />
                  Terminé : {stats.finished_at ? new Date(stats.finished_at).toLocaleString('fr-FR') : '—'}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Server className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <div className="text-white/60 text-sm">Aucune synchronisation effectuée pour l'instant.</div>
                <div className="text-white/40 text-xs mt-1">Configure la clé API puis clique sur "Synchroniser".</div>
              </div>
            )}
          </div>
        </div>

        {/* Comportement automatique */}
        <div className="mt-6 glass-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-[#ff8247]" />
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Comportement automatique</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            {[
              { i: CheckCircle2, t: 'Import 100% du catalogue', d: 'Toutes les catégories et services SMMHype sont importés (nom, catégorie, prix fournisseur, min, max, type, refill, cancel, description).' },
              { i: RefreshCw, t: 'Pas de doublons', d: 'Chaque service est identifié par son ID SMMHype — les mises à jour ne créent jamais de doublon.' },
              { i: TrendingUp, t: 'Prix de vente recalculés', d: `Formule : fournisseur × ${form.coefficient} (jamais sous fournisseur, marge min +15%).` },
              { i: AlertTriangle, t: 'Services retirés', d: 'Comportement configurable : désactivés, marqués épuisé, ou supprimés.' },
              { i: Clock, t: 'Sync automatique', d: 'Manuel, horaire, ou quotidien — au choix.' },
              { i: PlugZap, t: 'Commandes en temps réel', d: 'Créées via `add`, suivies via `status`, refill/cancel supportés.' }
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#ff8247]/10 flex items-center justify-center flex-shrink-0">
                  <f.i className="w-5 h-5 text-[#ff8247]" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{f.t}</div>
                  <div className="text-white/60 text-xs">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="btn-ghost inline-flex items-center gap-2 text-sm">Retour au site <ChevronRight className="w-4 h-4" /></Link>
        </div>
      </div>

      <Footer showCTA={false} />
    </div>
  );
};

export default AdminSMMHype;
